import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Tabs,
  Tab,
  Alert,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  WhatsApp as WhatsAppIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  Work as WorkIcon,
  BusinessCenter as BusinessCenterIcon,
  PersonAdd as PersonAddIcon,
  Forum as ForumIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getApiBaseUrl, getAuthHeader } from '../../services/httpClient';
import {
  saveTemplatesLocally,
  loadBillCompletionOptions,
  loadMessageOptions,
  normalizeMessageOptions,
  applyMessageOptions,
  buildCustomerLoginUrl,
} from '../../services/whatsappTemplateService';

const tabLabels = ['Bill Completion', 'Work Status', 'Job Alert', 'Welcome', 'Enquiry', 'Student Registration'];
const tabTypes = ['billCompletion', 'workStatus', 'jobAlert', 'welcome', 'enquiry', 'studentRegistration'];

const CLIENT_DEFAULT_TEMPLATES = {
  billCompletion: `**👋 Hi Mr/Ms {name}! (హాయ్ {name} గారు!)** **💐 {businessName} ను ఎంచుకున్నందుకు ధన్యవాదాలు.**

మీ బిల్‌తో పాటు మీకు ఒక Special KNLS Coupon లభించింది. 🎁

✅ ₹150 విలువైన PVC Aadhaar Card కేవలం ₹75కే!

ఇంకా ఇతర ప్రత్యేక ఆఫర్లు కూడా మీ అకౌంట్‌లో అందుబాటులో ఉన్నాయి.

👉 మీ కూపన్లు చూడటానికి:
{dashboardUrl}

⏳ Offers పరిమిత కాలం మాత్రమే.

📞 {businessPhone}`,
  workStatus: `*Work Status — {businessName}*

*Bill No:* {billNo}
*Customer:* {customerName}
*Date:* {billDate}

*Services:*
{servicesList}

*Status:* {status}
{remarks}

📱 {businessPhone}`,
  jobAlert: `🎯 *{jobTitle}*
{jobUrl}

— {businessName}
📱 {businessPhone}`,
  welcome: `Welcome to {businessName}!

📱 {businessPhone}
📍 {businessAddress}
{servicesUrl}`,
  enquiry: `Hi {name}, thanks for contacting {businessName}.

📱 {businessPhone}
{trainingProgramsUrl}
{jobAlertsUrl}`,
  studentRegistration: `🎓 *Student Login Credentials*

*Student Name:* {studentName}
*Student ID:* {studentId}
*Phone:* {phone}
*Password:* {password}

👉 *Click here to login automatically:*
{autoLoginUrl}

Or visit: {studentLoginUrl}

📱 *Install KNLS Student App:*
{studentAppPlayStoreUrl}

_This link will automatically log you in to your student dashboard._`,
};

const mergeTemplateDefaults = (templates = {}, defaults = {}) => {
  const merged = { ...CLIENT_DEFAULT_TEMPLATES, ...defaults };
  tabTypes.forEach((type) => {
    if (templates[type] != null && String(templates[type]).trim()) {
      merged[type] = templates[type];
    }
  });
  return merged;
};

const MESSAGE_OPTION_CONFIG = {
  billCompletion: [
    ['includeCustomerName', 'Include Customer Name'],
    ['includeServiceName', 'Include Service Name'],
    ['includeCouponMessage', 'Include Coupon Message'],
    ['includePaymentDetails', 'Include Payment Details'],
    ['includePaymentStatus', 'Include Payment Status'],
    ['includePaymentMethods', 'Include Payment Methods'],
    ['includeTotalAmount', 'Include Total Paid'],
    ['includeDueAmount', 'Include Due Amount'],
    ['includeChangeAmount', 'Include Change Amount'],
    ['includeWorkStatus', 'Include Work Status'],
    ['includeDashboardUrl', 'Include Coupon / Dashboard Link'],
    ['includeServicesUrl', 'Include Services Link'],
  ],
  workStatus: [
    ['includeBillNo', 'Include Bill Number'],
    ['includeCustomerName', 'Include Customer Name'],
    ['includeBillDate', 'Include Bill Date'],
    ['includeServicesList', 'Include Services List'],
    ['includeStatus', 'Include Status'],
    ['includeRemarks', 'Include Remarks'],
    ['includeServiceNames', 'Include Service Names'],
  ],
  jobAlert: [
    ['includeJobTitle', 'Include Job Title'],
    ['includeCategory', 'Include Category'],
    ['includeOrganization', 'Include Organization'],
    ['includeVacancies', 'Include Vacancies'],
    ['includeQualification', 'Include Qualification'],
    ['includeLastDate', 'Include Last Date'],
    ['includeLocation', 'Include Location'],
    ['includeSalary', 'Include Salary'],
    ['includeAgeLimit', 'Include Age Limit'],
    ['includeJobUrl', 'Include Job Details Link'],
    ['includeBasePageUrl', 'Include Job Alerts Page Link'],
  ],
  welcome: [
    ['includeServicesUrl', 'Include Services Link'],
    ['includeCoursesUrl', 'Include Courses Link'],
    ['includeBaseUrl', 'Include Website Link'],
  ],
  enquiry: [
    ['includeName', 'Include Customer Name'],
    ['includeTrainingProgramsUrl', 'Include Training Programs Link'],
    ['includeJobAlertsUrl', 'Include Job Alerts Link'],
  ],
  studentRegistration: [
    ['includeStudentName', 'Include Student Name'],
    ['includeStudentId', 'Include Student ID'],
    ['includePhone', 'Include Phone Number'],
    ['includePassword', 'Include Password'],
    ['includeAutoLoginUrl', 'Include Auto-login Link'],
    ['includeStudentLoginUrl', 'Include Student Login Page'],
    ['includeStudentAppPlayStoreUrl', 'Include Play Store App Link'],
  ],
};

const WhatsAppMessageConfig = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [templates, setTemplates] = useState({});
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ownerId, setOwnerId] = useState(null);
  const [businessContext, setBusinessContext] = useState({});
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    contact_person_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    email: '',
  });

  const [messageOptions, setMessageOptions] = useState(loadMessageOptions());
  const billCompletionOptions = messageOptions.billCompletion || loadBillCompletionOptions();

  const sampleData = {
    billCompletion: {
      name: 'John Doe',
      serviceName: 'PAN Card Application',
      paymentInfo: '**💳 Payment Received:**\n💵 Cash: ₹500.00\n**Total Paid: ₹500.00**\n',
      couponMessage: '',
      phone: '9999999999',
      last4Digits: '9999',
      dashboardUrl: buildCustomerLoginUrl('9999999999', 'https://services.knls.in'),
      servicesUrl: 'https://services.knls.in/services',
    },
    workStatus: {
      billNo: 'BILL-2024-001',
      customerName: 'John Doe',
      billDate: '15 Jan 2024',
      servicesList: '• PAN Card Application',
      status: 'Completed',
      remarks: '',
      serviceNames: 'PAN Card Application',
    },
    jobAlert: {
      jobTitle: 'Clerk Position',
      category: 'Government Jobs',
      organization: 'State Govt',
      vacancies: '📊 Vacancies: 50\n',
      qualification: '',
      lastDate: '📅 Last Date: 30 Jan 2024\n',
      location: '',
      salary: '',
      ageLimit: '',
      jobUrl: 'https://services.knls.in/jobalerts?job=123',
      basePageUrl: 'https://services.knls.in/jobalerts',
      categoryHashtag: 'GovernmentJobs',
    },
    welcome: {
      servicesUrl: 'https://services.knls.in/services',
      coursesUrl: 'https://services.knls.in/courses',
      baseUrl: 'https://services.knls.in',
    },
    enquiry: {
      name: 'John Doe',
      trainingProgramsUrl: 'https://services.knls.in/training-programs',
      jobAlertsUrl: 'https://services.knls.in/jobalerts/',
    },
    studentRegistration: {
      studentName: 'Rahul Kumar',
      studentId: 'STU-2026-001',
      phone: '9999999999',
      password: '99992006',
      autoLoginUrl: 'https://services.knls.in/student/login?phone=9999999999&auto=true&password=99992006',
      studentLoginUrl: 'https://services.knls.in/student/login',
      studentAppPlayStoreUrl: 'https://play.google.com/store/apps/details?id=com.knls.academy',
    },
  };

  const applyContextToSample = (type, ctx) => ({
    ...sampleData[type],
    businessName: ctx.businessName || '',
    shopName: ctx.shopName || ctx.businessName || '',
    businessPhone: ctx.businessPhone || '',
    businessEmail: ctx.businessEmail || '',
    businessAddress: ctx.businessAddress || '',
    contactPerson: ctx.contactPerson || '',
    servicesUrl: ctx.servicesUrl || sampleData[type].servicesUrl,
    coursesUrl: ctx.coursesUrl || sampleData.welcome.coursesUrl,
    baseUrl: ctx.baseUrl || sampleData.welcome.baseUrl,
    dashboardUrl: ctx.dashboardUrl || sampleData.billCompletion.dashboardUrl,
    trainingProgramsUrl: ctx.trainingProgramsUrl || sampleData.enquiry.trainingProgramsUrl,
    jobAlertsUrl: ctx.jobAlertsUrl || sampleData.enquiry.jobAlertsUrl,
  });

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${getApiBaseUrl()}/whatsapp-config`, {
        headers: { ...getAuthHeader() },
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load config');
      }
      const data = response.data.data;
      const defaultTemplatesWithFallback = mergeTemplateDefaults(
        data.defaultTemplates || {},
      );
      const templatesWithFallback = mergeTemplateDefaults(
        data.templates || {},
        defaultTemplatesWithFallback,
      );
      setTemplates(templatesWithFallback);
      setDefaultTemplates(defaultTemplatesWithFallback);
      setMessageOptions(
        normalizeMessageOptions(data.messageOptions, data.billCompletionOptions),
      );
      setOwnerId(data.ownerId);
      setBusinessContext(data.businessContext || {});
      setProfileForm({
        business_name: data.businessContext?.businessName || '',
        contact_person_name: data.businessContext?.contactPerson || '',
        address: data.businessContext?.address || '',
        city: data.businessContext?.city || '',
        state: data.businessContext?.state || '',
        pincode: data.businessContext?.pincode || '',
        email: data.businessContext?.businessEmail || '',
      });
      saveTemplatesLocally(
        data.ownerId,
        templatesWithFallback,
        data.billCompletionOptions,
        normalizeMessageOptions(data.messageOptions, data.billCompletionOptions),
      );
    } catch (err) {
      console.error(err);
      const fallbackTemplates = mergeTemplateDefaults();
      setTemplates(fallbackTemplates);
      setDefaultTemplates(fallbackTemplates);
      setMessageOptions(normalizeMessageOptions());
      toast.error(err.response?.data?.message || 'Failed to load WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const generatePreview = (type, tpl, ctx, options) => {
    let previewText = tpl || '';
    const data = applyMessageOptions(type, applyContextToSample(type, ctx), options);

    Object.keys(data).forEach((key) => {
      const placeholder = `{${key}}`;
      previewText = previewText.replace(
        new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        data[key] || '',
      );
    });
    return previewText.replace(/\n{3,}/g, '\n\n').trim();
  };

  useEffect(() => {
    const next = {};
    tabTypes.forEach((type) => {
      next[type] = generatePreview(
        type,
        templates[type] || defaultTemplates[type] || CLIENT_DEFAULT_TEMPLATES[type],
        businessContext,
        messageOptions,
      );
    });
    setPreviews(next);
  }, [templates, businessContext, messageOptions, activeTab]);

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${getApiBaseUrl()}/whatsapp-config`,
        {
          templates,
          billCompletionOptions,
          messageOptions,
        },
        { headers: { ...getAuthHeader() } },
      );
      saveTemplatesLocally(ownerId, templates, billCompletionOptions, messageOptions);
      toast.success('WhatsApp templates saved for your business');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save templates');
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    try {
      const response = await axios.put(
        `${getApiBaseUrl()}/whatsapp-config/profile`,
        profileForm,
        { headers: { ...getAuthHeader() } },
      );
      if (response.data?.success) {
        toast.success('Business details updated');
        await loadConfig();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const resetToDefaults = async () => {
    try {
      const response = await axios.put(
        `${getApiBaseUrl()}/whatsapp-config/reset`,
        {},
        { headers: { ...getAuthHeader() } },
      );
      if (response.data?.success) {
        setTemplates(response.data.data.templates || {});
        setMessageOptions(
          normalizeMessageOptions(
            response.data.data.messageOptions,
            response.data.data.billCompletionOptions,
          ),
        );
        toast.success('Reset to defaults using your business profile');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset');
    }
  };

  const getMessageTypeLabel = (type) =>
    ({
      billCompletion: 'Bill Completion',
      workStatus: 'Work Status Update',
      jobAlert: 'Job Alert Share',
      welcome: 'Welcome Message',
      enquiry: 'Enquiry Form Message',
      studentRegistration: 'Student Registration Message',
    })[type] || type;

  const getMessageTypeIcon = (type) =>
    ({
      billCompletion: <ReceiptIcon />,
      workStatus: <WorkIcon />,
      jobAlert: <BusinessCenterIcon />,
      welcome: <PersonAddIcon />,
      enquiry: <ForumIcon />,
      studentRegistration: <PersonAddIcon />,
    })[type] || <WhatsAppIcon />;

  const getPlaceholders = (type) => {
    const common = [
      '{businessName} - Your shop / business name',
      '{businessPhone} - Your phone from profile',
      '{businessAddress} - Address from profile',
      '{businessEmail} - Email from profile',
      '{contactPerson} - Contact person name',
    ];
    const specific = {
      billCompletion: [
        '{name} - Customer name',
        '{phone} - Customer phone (10 digits)',
        '{last4Digits} - Last 4 digits of phone (default password)',
        '{serviceName} - Service name',
        '{paymentInfo} - Payment details',
        '{couponMessage} - Coupon message block',
        '{dashboardUrl} - Auto-login → Coupons → Redeem → order',
        '{servicesUrl} - Services page',
      ],
      workStatus: [
        '{billNo} - Bill number',
        '{customerName} - Customer name',
        '{billDate} - Bill date',
        '{servicesList} - Services list',
        '{status} - Work status',
        '{remarks} - Remarks',
        '{serviceNames} - Service names',
      ],
      jobAlert: [
        '{jobTitle} - Job title',
        '{category} - Category',
        '{organization} - Organization',
        '{jobUrl} - Job link',
        '{basePageUrl} - Job alerts page',
      ],
      welcome: ['{servicesUrl}', '{coursesUrl}', '{baseUrl}'],
      enquiry: ['{name}', '{trainingProgramsUrl}', '{jobAlertsUrl}'],
      studentRegistration: [
        '{studentName} - Student name',
        '{studentId} - Student ID',
        '{phone} - Student phone number',
        '{password} - Student login password',
        '{autoLoginUrl} - Auto-login dashboard link',
        '{studentLoginUrl} - Student login page',
        '{studentAppPlayStoreUrl} - KNLS student app Play Store link',
      ],
    };
    return [...(specific[type] || []), ...common];
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading WhatsApp configuration…</Typography>
      </Box>
    );
  }

  const activeType = tabTypes[activeTab];
  const activeOptions = MESSAGE_OPTION_CONFIG[activeType] || [];
  const activeOptionValues = messageOptions[activeType] || {};
  const updateMessageOption = (key, checked) => {
    setMessageOptions((prev) =>
      normalizeMessageOptions({
        ...prev,
        [activeType]: {
          ...(prev[activeType] || {}),
          [key]: checked,
        },
      }),
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            WhatsApp Message Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Messages use your business name, phone and address from the profile below.
            Defaults are generated automatically; you can customize each template.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ResetIcon />} onClick={resetToDefaults}>
          Reset to defaults
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Business details (used in every WhatsApp message)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business / Shop name"
                value={profileForm.business_name}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, business_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact person"
                value={profileForm.contact_person_name}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, contact_person_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={profileForm.city}
                onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={profileForm.state}
                onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pincode"
                value={profileForm.pincode}
                onChange={(e) => setProfileForm({ ...profileForm, pincode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone (from login)"
                value={businessContext.businessPhone || ''}
                disabled
                helperText="Phone is your registered login number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              />
            </Grid>
          </Grid>
          <Button sx={{ mt: 2 }} variant="contained" onClick={saveProfile}>
            Save business details
          </Button>
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabLabels.map((label, index) => (
              <Tab
                key={label}
                label={label}
                icon={getMessageTypeIcon(tabTypes[index])}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {getMessageTypeLabel(activeType)} Template
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {activeOptions.length > 0 && (
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Message options
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Tick or untick fields to update the preview instantly. Save all templates to apply
                    these options to outgoing WhatsApp messages.
                  </Typography>
                  <FormGroup row>
                    {activeOptions.map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={activeOptionValues[key] !== false}
                            onChange={(e) =>
                              updateMessageOption(key, e.target.checked)
                            }
                            size="small"
                          />
                        }
                        label={label}
                        sx={{ minWidth: { xs: '100%', sm: '45%', md: '30%' }, mr: 1 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              )}

              <TextField
                fullWidth
                multiline
                rows={18}
                value={templates[activeType] || ''}
                onChange={(e) =>
                  setTemplates({ ...templates, [activeType]: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    if (defaultTemplates[activeType]) {
                      setTemplates({
                        ...templates,
                        [activeType]: defaultTemplates[activeType],
                      });
                      toast.info('Loaded server default for this tab');
                    }
                  }}
                >
                  Use default for tab
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveTemplate}
                  disabled={saving}
                  fullWidth
                  sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#E66A00' } }}
                >
                  {saving ? 'Saving…' : 'Save all templates'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Preview (with your business details)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                fullWidth
                multiline
                rows={18}
                value={previews[activeType] || ''}
                InputProps={{ readOnly: true }}
                sx={{
                  mb: 2,
                  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' },
                }}
              />
              <Card sx={{ bgcolor: 'info.light' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <InfoIcon color="info" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Placeholders
                    </Typography>
                  </Box>
                  {getPlaceholders(activeType).map((line) => (
                    <Typography key={line} variant="body2" component="div">
                      • {line}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        Templates are saved per business account on the server. Bill, pending works, job alerts
        and enquiry flows use these messages automatically with your shop name and contact details.
      </Alert>
    </Box>
  );
};

export default WhatsAppMessageConfig;
